/**
 * Env vars. Валидируем Zod'ом при загрузке — если что-то не задано,
 * приложение падает сразу с понятным сообщением в консоли, а не
 * мистически в рантайме.
 */

import {z} from 'zod';

export type CountryCode = 'kg' | 'uz' | 'ru';

const EnvSchema = z.object({
  /**
   * Базовый адрес API. Живой гейтвей — `https://gateway.skrezo.com/api`,
   * он же лежит в `.env.production`.
   *
   * Хвост `/api` обязателен: http-слой добавляет к базе только `/v1`, а у
   * гейтвея пути вида `/api/v1/admin/...`. Без него приходит 404.
   *
   * ПУСТАЯ строка = мок-режим: фетчеры отдают mocks, http не вызывается.
   * Относительный `/api` — через Vite dev-proxy.
   *
   * Здесь был записан `https://gateway-v2.kassir.kg/skrz/api` — гейтвей
   * другого продукта, оставшийся от проекта, из которого админку
   * ответвили. В коде он не использовался, но читался как действующий
   * адрес: по такому комментарию легко уйти отлаживать не туда.
   */
  VITE_API_BASE_URL: z.string().default(''),
  VITE_COUNTRY_CODE: z.enum(['kg', 'uz', 'ru']).default('kg'),
  VITE_APP_VERSION: z.string().default('0.1.0'),

  /**
   * Опциональный таргет для Vite dev-proxy. Срабатывает когда
   * VITE_API_BASE_URL === '/api'. См. vite.config.ts.
   */
  VITE_DEV_PROXY_TARGET: z.string().default(''),

  /**
   * Задел под MSW / переключение на реальный BE. Сейчас data-слой
   * работает на моках (services/<domain>/mocks.ts), источник определяет фетчер.
   */
  VITE_ENABLE_MSW: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),

  /**
   * Dev-only: автозаполнение формы входа команды Skrezo (логин+пароль).
   * В prod-сборке игнорируется. Клиентский контур (телефон+OTP) не
   * автозаполняем — там нет пароля.
   */
  VITE_DEV_TEAM_LOGIN: z.string().default(''),
  VITE_DEV_TEAM_PASSWORD: z.string().default(''),
});

const parsed = EnvSchema.safeParse(import.meta.env);

if (!parsed.success) {
  console.error('[env] invalid env vars', parsed.error.flatten().fieldErrors);
  throw new Error('Invalid environment configuration. See console.');
}

export const env = parsed.data;

/** Живой гейтвей подключён; пусто в env — данные из моков */
export const apiEnabled = env.VITE_API_BASE_URL !== '';
